// Diet orders and the kitchen (docs/plans/phase3-kitchen-and-housekeeping.md §1.3). URLs carry
// IDs, a day and a meal; the kitchen note travels in bodies only.
import { queryOptions } from '@tanstack/react-query';
import type { Meal } from '@wodi/domain';
import {
  dietOptionsSchema,
  kitchenWardsSchema,
  mealListSchema,
  operationsListItemSchema,
  stayDietSchema,
  wardDietsSchema,
  type MealTicketsPrintedRequest,
  type PlaceDietOrderRequest,
  type SaveOperationsListRequest,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export const kitchenKeys = {
  all: ['kitchen'] as const,
  options: ['kitchen', 'options'] as const,
  wards: ['kitchen', 'wards'] as const,
  stay: (admissionId: string) => ['kitchen', 'stay', admissionId] as const,
  ward: (wardId: string) => ['kitchen', 'ward', wardId] as const,
  mealList: (wardId: string | null, date: string, meal: Meal) =>
    ['kitchen', 'meal-list', wardId ?? 'all', date, meal] as const,
};

export function fetchDietOptions() {
  return api('GET', '/diet/options', { schema: dietOptionsSchema });
}

export const dietOptionsQuery = queryOptions({
  queryKey: kitchenKeys.options,
  queryFn: fetchDietOptions,
});

export function stayDietQuery(admissionId: string) {
  return queryOptions({
    queryKey: kitchenKeys.stay(admissionId),
    queryFn: () => api('GET', `/admissions/${admissionId}/diet-orders`, { schema: stayDietSchema }),
  });
}

export function wardDietsQuery(wardId: string) {
  return queryOptions({
    queryKey: kitchenKeys.ward(wardId),
    queryFn: () => api('GET', `/wards/${wardId}/diets`, { schema: wardDietsSchema }),
  });
}

export const kitchenWardsQuery = queryOptions({
  queryKey: kitchenKeys.wards,
  queryFn: () => api('GET', '/kitchen/wards', { schema: kitchenWardsSchema }),
});

/** The meal list refreshes every minute: diets change during the day. */
export function mealListQuery(wardId: string | null, date: string, meal: Meal) {
  const ward = wardId === null ? '' : `wardId=${wardId}&`;
  return queryOptions({
    queryKey: kitchenKeys.mealList(wardId, date, meal),
    queryFn: () =>
      api('GET', `/kitchen/meal-list?${ward}date=${date}&meal=${meal}`, {
        schema: mealListSchema,
      }),
    refetchInterval: 60_000,
  });
}

export function placeDietOrder(admissionId: string, body: PlaceDietOrderRequest) {
  return api('POST', `/admissions/${admissionId}/diet-orders`, { body, schema: stayDietSchema });
}

export function dietOrderEnteredInError(dietOrderId: string, reason: string) {
  return api('POST', `/diet-orders/${dietOrderId}/entered-in-error`, {
    body: { reason },
    schema: stayDietSchema,
  });
}

export function recordMealTicketsPrinted(body: MealTicketsPrintedRequest) {
  return api('POST', '/kitchen/meal-tickets/printed', { body });
}

export function saveDietList(kind: 'diet_type' | 'diet_texture', body: SaveOperationsListRequest) {
  return api('PUT', `/diet/options/${kind}`, {
    body,
    schema: z.array(operationsListItemSchema),
  });
}
